import BaseModel from "./BaseModel";
import CertificateStatus from "@/dictionary/CertificateDictionary";
import CertificateAsset from "./CertificateAsset";
import DateHandler from "@/composables/handlers/DateHandler";
import { translate } from "@/composables/translate";

/**
 * Certificate 
 */
export default class Certificate extends BaseModel{

  constructor({
    id,
    user,
    user_name,
    user_uuid,
    uuid,
    course,
    course_title,
    course_uuid,
    instructor_names,
    duration_in_minutes,
    certificate_source,
    certificate_format,
    finished_at,
    status,
    certificate_assets,
    created_at,
    updated_at
  }) {
    super();

    this.dictionary = new CertificateStatus();

    this.id = id || null;
    this.user = user || null;
    this.user_name = user_name || null;
    this.user_uuid = user_uuid || null;
    this.uuid = uuid || null;
    this.course = course || null;
    this.course_title = course_title || null;
    this.course_uuid = course_uuid || null;
    this.instructor_names = instructor_names || null;
    this.duration_in_minutes = duration_in_minutes || null;
    this.certificate_source = certificate_source || null;
    this.certificate_format = certificate_format || null;
    this.certificate_assets = certificate_assets || null;
    this.finished_at = finished_at || null;
    this.status = status || null;
    this.created_at = created_at || null;
    this.updated_at = updated_at || null;

    this.hydrateCertificateAssets();
  }

  /**
   * Parses CertificateAssets to an object
   */
  hydrateCertificateAssets() {
    this.certificate_assets = new CertificateAsset({
      pdf_file: this.certificate_assets.pdf_file || null,
      svg_file: this.certificate_assets.svg_file || null,
    });

    this.certificate_assets.parseFiles();
  }

  /**
   * Checks if there is a valid certificate_assets files
   * @returns {boolean}
   */
  hasFiles() {
    return this.certificate_assets.hasFiles();
  }

  /**
   * Checks if there is the selected file
   * @returns {boolean}
   */
  hasFile(format = 'pdf') {
    const value = format == 'pdf' ? this.getPdfFile() : this.getSvgFile();
    return value ? true : false;
  }

  /**
   * Gets file
   * @param {string} format - either (pdf or jpeg)
   * @returns {string} 
   */
  getFile(format = '') {
    return this.certificate_assets.getFile(format);
  }

  /**
   * Gets PDF Certificate
   * @returns {string}
   */
  getPdfFile() {
    return this.certificate_assets.getFile('pdf');
  }

  /**
   * Gets SVG Certificate
   * @param {bool} addBase64Tag
   * @returns {string}
   */
  getSvgFile(addBase64Tag = false) {
    const file = this.certificate_assets.getFile('svg', false);

    return addBase64Tag ? file.replace('data:image/svg+xml;base64,', '') : file;
  }

  /**
   * Gets finished at date parsed
   * @returns {string}
   */
  getFinishedAtDate() {
    if (!this.finished_at) {
      return '';
    }

    return DateHandler.format(this.finished_at, 'DATE_FULL')
  }

  /**
   * Generates the file name to be exported
   * @param {string} format - either (pdf or svg)
   * @returns {string}
   */
  generateFileName(format = 'pdf') {
    let formats = {
      pdf: '.pdf',
      svg: '.svg'
    };

    const certificateFormat = formats[format] != undefined ? formats[format] : formats.pdf;
    return `TMV_cert_${this.uuid}${certificateFormat}`;
  }

  /**
   * Checks if Certificate belongs to this userId
   * @param {integer} userId
   * @returns {bool} 
   */
  isMyCertificate(userUuid = null) {
    return this.user_uuid == userUuid;
  }

  /**
   * Checks if Certificate is valid
   * @returns {bool}
   */
  isValid() {
    return this.status == this.dictionary.VALID;
  }

  /**
   * Checks if Certifiacte can have the user_name changed
   * Obs: Allows only one change for each 3 days
   * @return {bool}
   */
  canChangeCertificateName() {
    if (!this.updated_at) {
      return true;
    }

    const date = DateHandler.toDateTime(this.updated_at);
    const nowData = DateHandler.now();

    const difference = Math.ceil(nowData.diff(date, ['days']).days);
    const expectedDaysDiference = parseInt(process.env.VUE_APP_REQUEST_CERTIFICATE_THRESHOLD) || 3;

    return difference >= expectedDaysDiference;
  }

  /**
   * Gets the remaining time to allow a certificate name change request as text
   * @returns {string}
   */
  getRemainingTimeToAllowChangeNameRequest() {
    if (!this.updated_at) {
      return 0;
    }

    const date = DateHandler.toDateTime(this.updated_at);
    const nowData = DateHandler.now();

    const difference = nowData.diff(date).days;
    const expectedDaysDiference = parseInt(process.env.VUE_APP_REQUEST_CERTIFICATE_THRESHOLD) || 3;

    const remainingTime = expectedDaysDiference - difference;
    return remainingTime > 0 ? `${remainingTime} ${translate('day(s)')}` : `0 ${translate('day(s)')}`;
  }
}